import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Alert,
  Box,
  Button,
  Chip,
  Checkbox,
  Divider,
  Dialog,
  FormControlLabel,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import {
  getA3FamiliasTalla,
  getA3FamiliasTallaClasificadas,
  getA3PedidoLineasMatriz,
  getA3Tallas,
  getA3CentrosCosteAnaliticos,
  getA3ClienteCentrosAnaliticos,
  getA3PedidoCentrosCabecera,
  getA3Clientes,
  getA3Proveedores,
  searchA3Articulos,
} from "../../api/Produccion/a3ArticulosApi";
import {
  A3_EMPRESA_DB_SOURCE_OPTIONS,
  getEmpresaA3Meta,
} from "../../constants/a3Empresas";
import { normalizeFechaIso } from "./a3PedidoMatrizUtils";

/** Fila API A3: el driver puede devolver claves en distinto casing. */
function a3ProveedorNombre(row) {
  if (!row || typeof row !== "object") return "";
  return String(
    row.NOMBRE ??
      row.nombre ??
      row.NomPro ??
      row.nompro ??
      row.RAZON ??
      row.razon ??
      "",
  ).trim();
}

function a3ProveedorCodigo(row) {
  if (!row || typeof row !== "object") return "";
  return String(row.CODPRO ?? row.codpro ?? "").trim();
}

const normStr = (s) => String(s ?? "").trim();
const normText = (s) =>
  String(s ?? "")
    .trim()
    .toLowerCase();

/**
 * Cabecera superior = colTalla (familia matrixFamCol), lateral izquierda = rowTalla (familia matrixFamRow).
 * A3 guarda siempre CODFAMTALLAH/CODTALLAH (eje horizontal en pantalla ERP) y CODFAMTALLAV/CODTALLAV (vertical).
 * Si en el maestro H y V son familias distintas, orientamos la celda para que coincida con A3.
 */
function packLinepediFromGridPure(art, famCol, famRow, colTalla, rowTalla) {
  const fc = normStr(famCol);
  const fr = normStr(famRow);
  const ah = normStr(art?.CODFAMTALLAH ?? art?.codfamtallah ?? "");
  const av = normStr(art?.CODFAMTALLAV ?? art?.codfamtallav ?? "");
  const colCod = normStr(colTalla?.CODTALLA);
  const rowCod = normStr(rowTalla?.CODTALLA);
  if (!fc || !fr || !colCod || !rowCod) return null;
  if (ah && av && fc === ah && fr === av) {
    return {
      codFamTallaH: famCol,
      codTallaH: colCod,
      codFamTallaV: famRow,
      codTallaV: rowCod,
    };
  }
  if (ah && av && fc === av && fr === ah) {
    return {
      codFamTallaH: famRow,
      codTallaH: rowCod,
      codFamTallaV: famCol,
      codTallaV: colCod,
    };
  }
  return {
    codFamTallaH: famCol,
    codTallaH: colCod,
    codFamTallaV: famRow,
    codTallaV: rowCod,
  };
}

function lineasPackMatch(l, p) {
  return (
    p &&
    normStr(l.codFamTallaH) === normStr(p.codFamTallaH) &&
    normStr(l.codTallaH) === normStr(p.codTallaH) &&
    normStr(l.codFamTallaV) === normStr(p.codFamTallaV) &&
    normStr(l.codTallaV) === normStr(p.codTallaV)
  );
}

function normCentroCosteCod(c) {
  return String(c ?? "").trim();
}

function flagTrue(v) {
  const s = String(v ?? "")
    .trim()
    .toUpperCase();
  return v === true || v === 1 || s === "T" || s === "TRUE" || s === "1" || s === "S";
}

function centroField(row, ...keys) {
  for (const k of keys) {
    if (row?.[k] !== undefined && row?.[k] !== null) return row[k];
  }
  return "";
}

const STAGE_EMPRESA_CODES = new Set(["1", "2", "3"]);
const STAGE_DEPARTAMENTO_CODES = new Set(["6", "7", "8", "9", "98"]);

function toInputDate(raw) {
  if (!raw) return "";
  const normalized = normalizeFechaIso(raw);
  if (normalized) return normalized;
  if (typeof raw === "string") {
    const m = raw.trim().match(/^(\d{4}-\d{2}-\d{2})/);
    if (m) return m[1];
  }
  try {
    const d = new Date(raw);
    if (!Number.isFinite(d.getTime())) return "";
    return d.toISOString().slice(0, 10);
  } catch (_e) {
    return "";
  }
}

/** En edición con compra+venta, por defecto una sola fecha de entrega en A3 (FECENTREGA). */
function patchFechaEntregaPedido(prev, field, value, { incluirCompra, incluirVenta, fechasDistintas }) {
  const v = String(value || "").trim();
  const par = incluirCompra && incluirVenta && !fechasDistintas;
  if (field === "fecha") {
    if (par) return { ...prev, fecha: v, fechaCompra: v, fechaVenta: v };
    if (incluirVenta) return { ...prev, fecha: v, fechaVenta: v };
    if (incluirCompra) return { ...prev, fecha: v, fechaCompra: v };
    return { ...prev, fecha: v };
  }
  if (field === "fechaCompra") {
    if (par) return { ...prev, fecha: v, fechaCompra: v, fechaVenta: v };
    return { ...prev, fechaCompra: v };
  }
  if (field === "fechaVenta") {
    if (par) return { ...prev, fecha: v, fechaCompra: v, fechaVenta: v };
    return { ...prev, fechaVenta: v };
  }
  return prev;
}

const ProduccionPedidosDialog = ({
  open,
  onClose,
  articulo,
  presupuesto,
  clienteNombre,
  onConfirm,
  referenciaSuffix = "",
  /** Fila CRM (PresupuestoA3Pedidos): modo edición sin cambiar prov./cli./ref. */
  registroCrm = null,
  pedidosContextRows = [],
  initialDbSource = "STAGE_DESIGN",
  lockDbSource = false,
}) => {
  const isEdit = Boolean(registroCrm?.IdPresupuestoA3Pedido);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [proveedores, setProveedores] = useState([]);
  const [clientesA3, setClientesA3] = useState([]);
  const [familiasTallaCols, setFamiliasTallaCols] = useState([]);
  const [familiasTallaRows, setFamiliasTallaRows] = useState([]);
  const [tallasH, setTallasH] = useState([]);
  const [tallasV, setTallasV] = useState([]);
  const [lineasTallas, setLineasTallas] = useState([]);
  const [matrixFamiliaH, setMatrixFamiliaH] = useState("");
  const [matrixFamiliaV, setMatrixFamiliaV] = useState("");
  const [pedidoTeniaMatriz, setPedidoTeniaMatriz] = useState(false);
  const [loadingMatrizPedido, setLoadingMatrizPedido] = useState(false);
  const [confirmMismatchDialogOpen, setConfirmMismatchDialogOpen] = useState(false);
  const [centrosCatalogRows, setCentrosCatalogRows] = useState([]);
  /** Maestro A3 (incl. CODFAMTALLAH/V); en edición el padre suele pasar solo CODART. */
  const [articuloMaestro, setArticuloMaestro] = useState(null);
  /** Solo edición con compra+venta: permite FECENTREGA distinta en cada cabecera A3. */
  const [fechasDistintas, setFechasDistintas] = useState(false);
  const referenciaDefault = useMemo(() => {
    const base =
      presupuesto?.IdPresupuesto ||
      presupuesto?.ProductId ||
      presupuesto?.ABREVIATURA ||
      "REF";
    const cod = articulo?.CODART || articulo?.REFERENCIA || "ART";
    return `${base}-${cod}`.toUpperCase();
  }, [articulo, presupuesto]);

  const [form, setForm] = useState({
    articulo: "",
    proveedor: "",
    proveedorNombre: "",
    unidadesCompra: "",
    precioCoste: "",
    monedaCompra: "EUR",
    cliente: "",
    clienteNombre: "",
    unidadesVenta: "",
    precioVenta: "",
    monedaVenta: "EUR",
    fecha: "",
    fechaCompra: "",
    fechaVenta: "",
    referencia: "",
    /** compra | venta | ambos — solo alta; el API A3 lo usa en POST */
    modo: "ambos",
    /** 1 | 2 — usado por A3 para numeración por tipo contable */
    tipoContable: "1",
    /** STAGE_DESIGN | ERNA_TRADE | NML_SPAIN — combos cliente/proveedor */
    dbSource: "STAGE_DESIGN",
    /** Analítica A3: CENTROCOSTE, CENTROCOSTE2, CENTROCOSTE3 (empresa, marca, departamento) */
    centroCosteEmpresa: "",
    centroCosteMarca: "",
    centroCosteDepartamento: "",
  });

  const formatDecimal = (value) => {
    if (value === undefined || value === null || value === "") return "";
    const num = Number(value);
    return Number.isFinite(num) ? String(num) : String(value);
  };

  const formatPriceInput = (value) => {
    if (value === undefined || value === null || value === "") return "";
    const num = Number(value);
    return Number.isFinite(num) ? num.toFixed(2) : String(value);
  };

  const resolveUnitPriceForEdit = (
    unitsRaw,
    unitRaw,
    totalRaw,
    totalDocumentoRaw,
  ) => {
    const u = Number(unitsRaw);
    const p = Number(unitRaw);
    const t = Number(totalRaw);
    const td = Number(totalDocumentoRaw);
    const hasU = Number.isFinite(u) && u > 0;
    const hasP = Number.isFinite(p);
    const hasT = Number.isFinite(t);
    const hasTD = Number.isFinite(td);
    const baseTotal = hasTD ? td : t;
    const hasBaseTotal = Number.isFinite(baseTotal);
    if (!hasU) return hasP ? p : null;
    if (!hasP && hasBaseTotal) return Number((baseTotal / u).toFixed(6));
    if (hasP && hasBaseTotal) {
      const implied = Number((baseTotal / u).toFixed(6));
      if (Number.isFinite(implied) && Math.abs(implied - p) > 0.01) return implied;
    }
    return hasP ? p : hasBaseTotal ? Number((baseTotal / u).toFixed(6)) : null;
  };

  const toNumber = (value) => {
    const raw = String(value ?? "")
      .trim()
      .replace(",", ".");
    if (!raw) return 0;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const totalCoste = useMemo(() => {
    return toNumber(form.unidadesCompra) * toNumber(form.precioCoste);
  }, [form.unidadesCompra, form.precioCoste]);

  const totalVenta = useMemo(() => {
    return toNumber(form.unidadesVenta) * toNumber(form.precioVenta);
  }, [form.unidadesVenta, form.precioVenta]);

  const fmtMoney = (value) =>
    Number(value || 0).toLocaleString("es-ES", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  /** Sumatorios de la matriz = unidades, no importe (evita parecer moneda). */
  const fmtQtyMatriz = (value) =>
    Number(value || 0).toLocaleString("es-ES", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });

  const etiquetaMonedaCorta = (code) => {
    const c = String(code || "").trim().toUpperCase();
    if (c === "EURO") return "EUR";
    return c || "—";
  };

  const sortedProveedores = useMemo(() => {
    return [...proveedores].sort((a, b) => {
      const aLabel = String(a3ProveedorNombre(a) || a3ProveedorCodigo(a) || "")
        .trim()
        .toLowerCase();
      const bLabel = String(a3ProveedorNombre(b) || a3ProveedorCodigo(b) || "")
        .trim()
        .toLowerCase();
      return aLabel.localeCompare(bLabel, "es");
    });
  }, [proveedores]);

  const sortedClientesA3 = useMemo(() => {
    return [...clientesA3].sort((a, b) => {
      const aLabel = String(a?.NOMBRE || a?.CODCLI || "")
        .trim()
        .toLowerCase();
      const bLabel = String(b?.NOMBRE || b?.CODCLI || "")
        .trim()
        .toLowerCase();
      return aLabel.localeCompare(bLabel, "es");
    });
  }, [clientesA3]);

  useEffect(() => {
    if (!open) return;
    setError("");
    setArticuloMaestro(null);
    const today = new Date().toISOString().slice(0, 10);
    if (isEdit) {
      const r = registroCrm;
      const fcInit = toInputDate(r?.FechaCompraA3 || r?.FechaA3) || "";
      const fvInit = toInputDate(r?.FechaVentaA3 || r?.FechaA3) || "";
      setFechasDistintas(Boolean(fcInit && fvInit && fcInit !== fvInit));
      setLineasTallas([]);
      const famHArt = articulo?.CODFAMTALLAH ?? articulo?.codfamtallah ?? "";
      const famVArt = articulo?.CODFAMTALLAV ?? articulo?.codfamtallav ?? "";
      setMatrixFamiliaH(normStr(famHArt) ? String(famHArt).trim() : "");
      setMatrixFamiliaV(normStr(famVArt) ? String(famVArt).trim() : "");
      setPedidoTeniaMatriz(false);
      setForm({
        articulo: String(r?.CodArt || articulo?.CODART || "").trim(),
        modo: "ambos",
        proveedor: String(r?.CodPro || "").trim(),
        proveedorNombre: String(r?.ProveedorNombre || "").trim(),
        unidadesCompra:
          r?.UnidadesCompra != null && r.UnidadesCompra !== ""
            ? formatDecimal(r.UnidadesCompra)
            : String(presupuesto?.Units || 1),
        precioCoste:
          resolveUnitPriceForEdit(
            r?.UnidadesCompra,
            r?.PrecioCosteUn,
            r?.TotalCompra,
            r?.TotalDocumentoCompra,
          ) != null
            ? formatPriceInput(
                resolveUnitPriceForEdit(
                  r?.UnidadesCompra,
                  r?.PrecioCosteUn,
                  r?.TotalCompra,
                  r?.TotalDocumentoCompra,
                ),
              )
            : formatPriceInput(articulo?.PRCCOMPRA ?? articulo?.PRCCOSTE),
        monedaCompra: String(
          r?.MonedaCompraA3 || r?.MonedaCoste || articulo?.CODMON || "EUR",
        ).trim(),
        cliente: String(r?.CodCli || "").trim(),
        clienteNombre: String(r?.ClienteNombre || "").trim(),
        unidadesVenta:
          r?.UnidadesVenta != null && r.UnidadesVenta !== ""
            ? formatDecimal(r.UnidadesVenta)
            : String(presupuesto?.Units || 1),
        precioVenta:
          resolveUnitPriceForEdit(
            r?.UnidadesVenta,
            r?.PrecioVentaUn,
            r?.TotalVenta,
            r?.TotalDocumentoVenta,
          ) != null
            ? formatPriceInput(
                resolveUnitPriceForEdit(
                  r?.UnidadesVenta,
                  r?.PrecioVentaUn,
                  r?.TotalVenta,
                  r?.TotalDocumentoVenta,
                ),
              )
            : formatPriceInput(articulo?.PRCVENTA),
        monedaVenta: String(
          r?.MonedaVentaA3 || r?.MonedaVenta || articulo?.CODMONCOM || "EUR",
        ).trim(),
        fecha:
          toInputDate(
            r?.FechaA3 || r?.FechaVentaA3 || r?.FechaCompraA3 || r?.CreatedAt,
          ) || today,
        fechaCompra: toInputDate(r?.FechaCompraA3 || r?.FechaA3) || "",
        fechaVenta: toInputDate(r?.FechaVentaA3 || r?.FechaA3) || "",
        referencia: String(r?.Referencia || "").trim(),
        tipoContable:
          String(
            r?.TipoContableVenta ?? r?.TipoContableCompra ?? "1",
          ).trim() || "1",
        dbSource:
          String(r?.EmpresaA3 || "STAGE_DESIGN").trim() || "STAGE_DESIGN",
        centroCosteEmpresa: "",
        centroCosteMarca: "",
        centroCosteDepartamento: "",
      });
      return;
    }
    setFechasDistintas(false);
    const refBase = referenciaDefault + (referenciaSuffix || "");
    setLineasTallas([]);
    setMatrixFamiliaH("");
    setMatrixFamiliaV("");
    setPedidoTeniaMatriz(false);
    setForm({
      articulo: articulo?.CODART || articulo?.REFERENCIA || "",
      modo: "ambos",
      proveedor: String(articulo?.CODPRO || "").trim(),
      proveedorNombre: "",
      unidadesCompra: String(presupuesto?.Units || 1),
      precioCoste: formatPriceInput(articulo?.PRCCOMPRA ?? articulo?.PRCCOSTE),
      monedaCompra: String(articulo?.CODMON || "EUR").trim(),
      // En alta, el valor de cliente que llega del presupuesto puede ser ID interno CRM.
      // Se resuelve más abajo contra catálogo A3 y se fija CODCLI real.
      cliente: "",
      clienteNombre: String(clienteNombre || "").trim(),
      unidadesVenta: String(presupuesto?.Units || 1),
      precioVenta: formatPriceInput(articulo?.PRCVENTA),
      monedaVenta: String(articulo?.CODMONCOM || "EUR").trim(),
      fecha: today,
      fechaCompra: "",
      fechaVenta: "",
      referencia: refBase,
      tipoContable: "1",
      dbSource: String(initialDbSource || "STAGE_DESIGN").trim() || "STAGE_DESIGN",
      centroCosteEmpresa: "",
      centroCosteMarca: "",
      centroCosteDepartamento: "",
    });
    const famHArt = articulo?.CODFAMTALLAH ?? articulo?.codfamtallah ?? "";
    const famVArt = articulo?.CODFAMTALLAV ?? articulo?.codfamtallav ?? "";
    if (normStr(famHArt)) setMatrixFamiliaH(String(famHArt).trim());
    if (normStr(famVArt)) setMatrixFamiliaV(String(famVArt).trim());
  }, [
    open,
    isEdit,
    registroCrm,
    articulo,
    presupuesto,
    clienteNombre,
    referenciaDefault,
    referenciaSuffix,
    initialDbSource,
  ]);

  useEffect(() => {
    if (!open || isEdit) return;
    const cod = String(articulo?.CODART || articulo?.REFERENCIA || "").trim();
    if (!cod) return;
    let cancelled = false;
    searchA3Articulos(cod, form.dbSource || "STAGE_DESIGN")
      .then((data) => {
        if (cancelled) return;
        const ar = Array.isArray(data?.rows) ? data.rows : [];
        const hit =
          ar.find((x) => normStr(x.CODART ?? x.codart) === normStr(cod)) ??
          ar[0] ??
          null;
        setArticuloMaestro(hit);
      })
      .catch(() => {
        if (!cancelled) setArticuloMaestro(null);
      });
    return () => {
      cancelled = true;
    };
  }, [open, isEdit, articulo?.CODART, articulo?.REFERENCIA, form.dbSource]);

  useEffect(() => {
    if (!open || isEdit || !articuloMaestro) return;
    const ah = normStr(articuloMaestro.CODFAMTALLAH ?? articuloMaestro.codfamtallah);
    const av = normStr(articuloMaestro.CODFAMTALLAV ?? articuloMaestro.codfamtallav);
    if (ah && av && ah !== av) {
      setMatrixFamiliaH(
        String(articuloMaestro.CODFAMTALLAH ?? articuloMaestro.codfamtallah ?? "").trim(),
      );
      setMatrixFamiliaV(
        String(articuloMaestro.CODFAMTALLAV ?? articuloMaestro.codfamtallav ?? "").trim(),
      );
    }
  }, [open, isEdit, articuloMaestro]);

  useEffect(() => {
    if (!open) return;
    const load = async () => {
      try {
        const [provData, clientesData, familiasData, familiasClasData] = await Promise.all([
          getA3Proveedores("", form.dbSource || "STAGE_DESIGN"),
          getA3Clientes("", form.dbSource || "STAGE_DESIGN"),
          getA3FamiliasTalla("", form.dbSource || "STAGE_DESIGN"),
          getA3FamiliasTallaClasificadas("", form.dbSource || "STAGE_DESIGN"),
        ]);
        const provRows = Array.isArray(provData?.rows) ? provData.rows : [];
        const clientesRows = Array.isArray(clientesData?.rows)
          ? clientesData.rows
          : [];
        setProveedores(provRows);
        setClientesA3(clientesRows);
        const famAll = Array.isArray(familiasData?.rows) ? familiasData.rows : [];
        const cols = Array.isArray(familiasClasData?.rowsTalla)
          ? familiasClasData.rowsTalla
          : [];
        const rowsFam = Array.isArray(familiasClasData?.rowsColor)
          ? familiasClasData.rowsColor
          : [];
        setFamiliasTallaCols(cols.length ? cols : famAll);
        setFamiliasTallaRows(rowsFam.length ? rowsFam : famAll);
      } catch (e) {
        setError(e?.response?.data?.error || e.message);
        setProveedores([]);
        setClientesA3([]);
        setFamiliasTallaCols([]);
        setFamiliasTallaRows([]);
      }
    };
    load();
  }, [open, form.dbSource]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    getA3CentrosCosteAnaliticos(form.dbSource || "STAGE_DESIGN")
      .then((data) => {
        if (cancelled) return;
        setCentrosCatalogRows(Array.isArray(data?.rows) ? data.rows : []);
      })
      .catch(() => {
        if (!cancelled) setCentrosCatalogRows([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, form.dbSource]);

  useEffect(() => {
    if (!open || isEdit) return;
    const c = String(form.cliente || "").trim();
    if (!c) return;
    let cancelled = false;
    getA3ClienteCentrosAnaliticos(c, form.dbSource || "STAGE_DESIGN")
      .then((data) => {
        if (cancelled) return;
        setForm((p) => ({
          ...p,
          centroCosteEmpresa: String(data?.centroCosteEmpresa || "").trim(),
          centroCosteMarca: String(data?.centroCosteMarca || "").trim(),
          centroCosteDepartamento: String(
            data?.centroCosteDepartamento || "",
          ).trim(),
        }));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [open, isEdit, form.cliente, form.dbSource]);

  useEffect(() => {
    if (!open || !isEdit || !registroCrm) return;
    const ds = String(
      registroCrm?.EmpresaA3 || form.dbSource || "STAGE_DESIGN",
    ).trim();
    let cancelled = false;
    getA3PedidoCentrosCabecera({
      idPedv: registroCrm?.IdPedv,
      idPedc: registroCrm?.IdPedc,
      dbSource: ds,
    })
      .then((data) => {
        if (cancelled) return;
        setForm((p) => ({
          ...p,
          centroCosteEmpresa: String(data?.centroCosteEmpresa || "").trim(),
          centroCosteMarca: String(data?.centroCosteMarca || "").trim(),
          centroCosteDepartamento: String(
            data?.centroCosteDepartamento || "",
          ).trim(),
        }));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [
    open,
    isEdit,
    registroCrm?.IdPresupuestoA3Pedido,
    registroCrm?.IdPedv,
    registroCrm?.IdPedc,
    registroCrm?.EmpresaA3,
    form.dbSource,
  ]);

  const centrosOptEmpresa = useMemo(() => {
    const arr = Array.isArray(centrosCatalogRows) ? centrosCatalogRows : [];
    if (!arr.length) return [];
    const isStage = String(form.dbSource || "STAGE_DESIGN").trim().toUpperCase() === "STAGE_DESIGN";
    const byStageFixed = arr.filter((r) =>
      STAGE_EMPRESA_CODES.has(
        normCentroCosteCod(centroField(r, "codigo", "CENTROCOSTE")),
      ),
    );
    if (isStage && byStageFixed.length) return byStageFixed;
    const byFlags = arr.filter((r) =>
      flagTrue(centroField(r, "esEmpresa", "APLINIVEL1")) &&
      !flagTrue(centroField(r, "esMarca", "APLINIVEL2")) &&
      !flagTrue(centroField(r, "esDepartamento", "APLINIVEL3")),
    );
    if (byFlags.length) return byFlags;
    return arr;
  }, [centrosCatalogRows, form.dbSource]);

  const centrosOptMarca = useMemo(() => {
    const arr = Array.isArray(centrosCatalogRows) ? centrosCatalogRows : [];
    if (!arr.length) return [];
    const isStage =
      String(form.dbSource || "STAGE_DESIGN").trim().toUpperCase() ===
      "STAGE_DESIGN";
    if (isStage) {
      const byStageFixed = arr.filter((r) => {
        const cod = normCentroCosteCod(centroField(r, "codigo", "CENTROCOSTE"));
        return (
          cod &&
          !STAGE_EMPRESA_CODES.has(cod) &&
          !STAGE_DEPARTAMENTO_CODES.has(cod)
        );
      });
      if (byStageFixed.length) return byStageFixed;
    }
    const byFlags = arr.filter((r) =>
      flagTrue(centroField(r, "esMarca", "APLINIVEL2")) &&
      !flagTrue(centroField(r, "esEmpresa", "APLINIVEL1")) &&
      !flagTrue(centroField(r, "esDepartamento", "APLINIVEL3")),
    );
    if (byFlags.length) return byFlags;
    return arr;
  }, [centrosCatalogRows, form.dbSource]);

  const centrosOptDepartamento = useMemo(() => {
    const arr = Array.isArray(centrosCatalogRows) ? centrosCatalogRows : [];
    if (!arr.length) return [];
    const isStage =
      String(form.dbSource || "STAGE_DESIGN").trim().toUpperCase() ===
      "STAGE_DESIGN";
    const byStageFixed = arr.filter((r) =>
      STAGE_DEPARTAMENTO_CODES.has(
        normCentroCosteCod(centroField(r, "codigo", "CENTROCOSTE")),
      ),
    );
    if (isStage && byStageFixed.length) return byStageFixed;
    const byFlags = arr.filter((r) =>
      flagTrue(centroField(r, "esDepartamento", "APLINIVEL3")) &&
      !flagTrue(centroField(r, "esEmpresa", "APLINIVEL1")) &&
      !flagTrue(centroField(r, "esMarca", "APLINIVEL2")),
    );
    if (byFlags.length) return byFlags;
    return arr;
  }, [centrosCatalogRows]);

  const centroCosteDescPorCodigo = useMemo(() => {
    const m = new Map();
    for (const r of Array.isArray(centrosCatalogRows) ? centrosCatalogRows : []) {
      const k = normCentroCosteCod(centroField(r, "codigo", "CENTROCOSTE"));
      if (!k) continue;
      m.set(k, String(centroField(r, "descripcion", "DESCCENTRO") ?? "").trim());
    }
    return m;
  }, [centrosCatalogRows]);

  const labelCentroCosteOpcion = (codigo) => {
    const c = normCentroCosteCod(codigo);
    if (!c) return "";
    if (!centroCosteDescPorCodigo.has(c)) return c;
    const d = centroCosteDescPorCodigo.get(c);
    const tail = d != null && d !== "" ? d : c;
    return `${c} — ${tail}`;
  };

  const buildCentroOptions = (rows, currentCode) => {
    const map = new Map();
    for (const r of Array.isArray(rows) ? rows : []) {
      const code = normCentroCosteCod(centroField(r, "codigo", "CENTROCOSTE"));
      if (!code) continue;
      map.set(code, { code, label: labelCentroCosteOpcion(code) });
    }
    const current = normCentroCosteCod(currentCode);
    if (current && !map.has(current)) {
      map.set(current, { code: current, label: labelCentroCosteOpcion(current) });
    }
    return Array.from(map.values()).sort((a, b) =>
      String(a.label || "").localeCompare(String(b.label || ""), "es", {
        sensitivity: "base",
      }),
    );
  };

  const empresaCentroOptions = useMemo(
    () => buildCentroOptions(centrosOptEmpresa, form.centroCosteEmpresa),
    [centrosOptEmpresa, form.centroCosteEmpresa],
  );
  const marcaCentroOptions = useMemo(
    () => buildCentroOptions(centrosOptMarca, form.centroCosteMarca),
    [centrosOptMarca, form.centroCosteMarca],
  );
  const departamentoCentroOptions = useMemo(
    () =>
      buildCentroOptions(centrosOptDepartamento, form.centroCosteDepartamento),
    [centrosOptDepartamento, form.centroCosteDepartamento],
  );

  useEffect(() => {
    if (!open || isEdit || !form.proveedor) return;
    const item = proveedores.find(
      (x) => a3ProveedorCodigo(x) === String(form.proveedor || "").trim(),
    );
    const nom = a3ProveedorNombre(item);
    if (!nom) return;
    setForm((p) =>
      p.proveedorNombre === nom ? p : { ...p, proveedorNombre: nom },
    );
  }, [open, isEdit, form.proveedor, proveedores]);

  useEffect(() => {
    if (!open || !form.cliente) return;
    const code = String(form.cliente || "").trim();
    const item = clientesA3.find(
      (x) => String(x?.CODCLI || "").trim() === code,
    );
    const nom = String(item?.NOMBRE || "").trim();
    if (!nom) return;
    setForm((p) => (p.clienteNombre === nom ? p : { ...p, clienteNombre: nom }));
  }, [open, form.cliente, clientesA3]);

  useEffect(() => {
    if (!open || isEdit) return;
    const rows = Array.isArray(clientesA3) ? clientesA3 : [];
    if (!rows.length) return;

    const currentCode = String(form.cliente || "").trim();
    const hasCurrentCode = currentCode
      ? rows.some((x) => String(x?.CODCLI || "").trim() === currentCode)
      : false;
    if (hasCurrentCode) return;

    const hint = String(form.clienteNombre || form.cliente || "").trim();
    if (!hint) return;
    const hintNorm = normText(hint);

    let hit =
      rows.find((x) => normText(x?.NOMBRE) === hintNorm) ||
      rows.find((x) => normText(x?.RAZON) === hintNorm) ||
      null;
    if (!hit) {
      const contains = rows.filter(
        (x) =>
          normText(x?.NOMBRE).includes(hintNorm) ||
          normText(x?.RAZON).includes(hintNorm),
      );
      if (contains.length === 1) hit = contains[0];
    }
    if (!hit) return;

    const code = String(hit?.CODCLI || "").trim();
    const name = String(hit?.NOMBRE || hit?.RAZON || "").trim();
    if (!code) return;
    if (code === currentCode && (!name || name === String(form.clienteNombre || "").trim())) return;
    setForm((p) => ({
      ...p,
      cliente: code,
      clienteNombre: name || p.clienteNombre,
    }));
  }, [open, isEdit, clientesA3, form.cliente, form.clienteNombre]);

  useEffect(() => {
    if (!open || !matrixFamiliaH) {
      setTallasH([]);
      return;
    }
    let active = true;
    getA3Tallas(matrixFamiliaH, "", form.dbSource || "STAGE_DESIGN")
      .then((data) => {
        if (!active) return;
        setTallasH(Array.isArray(data?.rows) ? data.rows : []);
      })
      .catch(() => {
        if (!active) return;
        setTallasH([]);
      });
    return () => {
      active = false;
    };
  }, [open, matrixFamiliaH, form.dbSource]);

  useEffect(() => {
    if (!open || !matrixFamiliaV) {
      setTallasV([]);
      return;
    }
    let active = true;
    getA3Tallas(matrixFamiliaV, "", form.dbSource || "STAGE_DESIGN")
      .then((data) => {
        if (!active) return;
        setTallasV(Array.isArray(data?.rows) ? data.rows : []);
      })
      .catch(() => {
        if (!active) return;
        setTallasV([]);
      });
    return () => {
      active = false;
    };
  }, [open, matrixFamiliaV, form.dbSource]);

  useEffect(() => {
    if (!open || !isEdit || !registroCrm) return;
    let cancelled = false;
    setLoadingMatrizPedido(true);
    (async () => {
      try {
        const codart = String(
          registroCrm?.CodArt || articulo?.CODART || "",
        ).trim();
        const idPedc = registroCrm?.IdPedc;
        const idPedv = registroCrm?.IdPedv;
        if (!codart || (!idPedc && !idPedv)) {
          if (!cancelled) {
            setPedidoTeniaMatriz(false);
            setLineasTallas([]);
            setMatrixFamiliaH("");
            setMatrixFamiliaV("");
          }
          return;
        }
        const [data, artSearch] = await Promise.all([
          getA3PedidoLineasMatriz({
            idPedc,
            idPedv,
            codart,
            dbSource: String(
              registroCrm?.EmpresaA3 || form.dbSource || "STAGE_DESIGN",
            ).trim(),
          }),
          searchA3Articulos(
            codart,
            String(registroCrm?.EmpresaA3 || form.dbSource || "STAGE_DESIGN").trim(),
          ).catch(() => ({ rows: [] })),
        ]);
        if (cancelled) return;
        const ar = Array.isArray(artSearch?.rows) ? artSearch.rows : [];
        const maestroHit =
          ar.find((x) => normStr(x.CODART ?? x.codart) === normStr(codart)) ??
          ar[0] ??
          null;
        setArticuloMaestro(maestroHit);
        const rows = Array.isArray(data?.rows) ? data.rows : [];
        if (rows.length > 0) {
          setPedidoTeniaMatriz(true);
          setLineasTallas(
            rows.map((x) => ({
              codFamTallaH: String(x.codFamTallaH || "").trim(),
              codTallaH: String(x.codTallaH || "").trim(),
              codFamTallaV: String(x.codFamTallaV || "").trim(),
              codTallaV: String(x.codTallaV || "").trim(),
              unidades: Number(x.unidades) || 0,
            })),
          );
          const ah = normStr(maestroHit?.CODFAMTALLAH ?? maestroHit?.codfamtallah);
          const av = normStr(maestroHit?.CODFAMTALLAV ?? maestroHit?.codfamtallav);
          if (ah && av && ah !== av) {
            setMatrixFamiliaH(
              String(maestroHit.CODFAMTALLAH ?? maestroHit.codfamtallah ?? "").trim(),
            );
            setMatrixFamiliaV(
              String(maestroHit.CODFAMTALLAV ?? maestroHit.codfamtallav ?? "").trim(),
            );
          } else {
            setMatrixFamiliaH(String(rows[0].codFamTallaH || "").trim());
            setMatrixFamiliaV(String(rows[0].codFamTallaV || "").trim());
          }
        } else {
          setPedidoTeniaMatriz(false);
          setLineasTallas([]);
          setMatrixFamiliaH("");
          setMatrixFamiliaV("");
        }
      } catch (_e) {
        if (!cancelled) {
          setPedidoTeniaMatriz(false);
          setLineasTallas([]);
          setMatrixFamiliaH("");
          setMatrixFamiliaV("");
        }
      } finally {
        if (!cancelled) setLoadingMatrizPedido(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, isEdit, registroCrm, articulo, form.dbSource]);

  const modoCreacion = form.modo || "ambos";
  const incluirCompra = isEdit
    ? Boolean(registroCrm?.IdPedc)
    : modoCreacion === "compra" || modoCreacion === "ambos";
  const incluirVenta = isEdit
    ? Boolean(registroCrm?.IdPedv)
    : modoCreacion === "venta" || modoCreacion === "ambos";
  const parCompraVentaEdit = isEdit && incluirCompra && incluirVenta;
  const totalMatriz = useMemo(
    () => lineasTallas.reduce((acc, row) => acc + toNumber(row.unidades), 0),
    [lineasTallas],
  );
  const unidadesEstimadasCabecera = useMemo(() => {
    const fromPresupuesto = toNumber(presupuesto?.Units);
    if (fromPresupuesto > 0) return fromPresupuesto;
    const fromRegistroUnits = toNumber(registroCrm?.Units);
    if (fromRegistroUnits > 0) return fromRegistroUnits;
    // En ProduccionSummary (modo edición) no siempre llega presupuesto;
    // usamos la mejor aproximación disponible desde la fila CRM.
    return Math.max(
      toNumber(registroCrm?.UnidadesVenta),
      toNumber(registroCrm?.UnidadesCompra),
      0,
    );
  }, [
    presupuesto?.Units,
    registroCrm?.Units,
    registroCrm?.UnidadesVenta,
    registroCrm?.UnidadesCompra,
  ]);
  const unidadesPedidasCabecera = useMemo(() => {
    const codart = String(form.articulo || "").trim().toUpperCase();
    if (!codart || !Array.isArray(pedidosContextRows) || !pedidosContextRows.length) {
      return 0;
    }
    const idPresupuestoRef = Number(
      registroCrm?.IdPresupuesto ?? presupuesto?.IdPresupuesto ?? 0,
    );
    return pedidosContextRows.reduce((acc, row) => {
      if (String(row?.CodArt || "").trim().toUpperCase() !== codart) return acc;
      if (idPresupuestoRef > 0) {
        const rowIdPresupuesto = Number(row?.IdPresupuesto || 0);
        if (rowIdPresupuesto > 0 && rowIdPresupuesto !== idPresupuestoRef) return acc;
      }
      const uc = toNumber(row?.UnidadesCompra);
      const uv = toNumber(row?.UnidadesVenta);
      return acc + Math.max(uc, uv, 0);
    }, 0);
  }, [form.articulo, pedidosContextRows, presupuesto?.IdPresupuesto, registroCrm?.IdPresupuesto]);
  const unitsCabeceraEstimate = useMemo(
    () => toNumber(presupuesto?.Units),
    [presupuesto?.Units],
  );
  const unitsCabeceraPedido = useMemo(() => {
    if (incluirCompra && incluirVenta) {
      return Math.max(toNumber(form.unidadesCompra), toNumber(form.unidadesVenta), 0);
    }
    if (incluirCompra) return toNumber(form.unidadesCompra);
    if (incluirVenta) return toNumber(form.unidadesVenta);
    return 0;
  }, [incluirCompra, incluirVenta, form.unidadesCompra, form.unidadesVenta]);
  const mismatchMatrizVsCabeceraPedido =
    lineasTallas.length > 0 &&
    unitsCabeceraPedido > 0 &&
    Math.abs(totalMatriz - unitsCabeceraPedido) > 0.0001;

  const mostrarMatriz =
    (!isEdit && modoCreacion === "ambos") ||
    (isEdit && incluirCompra && incluirVenta);

  const artForPack = articuloMaestro || articulo;
  const artFamilyH = normStr(
    artForPack?.CODFAMTALLAH ?? artForPack?.codfamtallah ?? "",
  );
  const artFamilyV = normStr(
    artForPack?.CODFAMTALLAV ?? artForPack?.codfamtallav ?? "",
  );
  // Solo bloquear cuando el artículo trae familias explícitas H/V desde A3.
  // Si no trae familias, se permite elegir y ajustar manualmente.
  const lockFamiliesToArticulo = Boolean(artFamilyH && artFamilyV);
  const resolvedFamilyH = artFamilyH || normStr(matrixFamiliaH);
  const resolvedFamilyV = artFamilyV || normStr(matrixFamiliaV);
  const familyScore = (f, kind) => {
    const txt = `${normStr(f?.CODFAMTALLA)} ${normStr(f?.DESCFAMTALLA)}`.toLowerCase();
    const tallaHints = /(talla|size|medida|numeraci[oó]n|num)/i;
    const colorHints = /(color|colores|colour|accesor|accessor|acc)/i;
    if (kind === "talla") return tallaHints.test(txt) ? 2 : colorHints.test(txt) ? 0 : 1;
    return colorHints.test(txt) ? 2 : tallaHints.test(txt) ? 0 : 1;
  };
  const sortByKind = (list, kind) =>
    [...list].sort((a, b) => familyScore(b, kind) - familyScore(a, kind));

  const familiesColumnOptions = lockFamiliesToArticulo
    ? familiasTallaCols.filter((f) => normStr(f.CODFAMTALLA) === resolvedFamilyH)
    : sortByKind(
        familiasTallaCols.filter(
          (f) =>
            !normStr(matrixFamiliaV) ||
            normStr(f.CODFAMTALLA) !== normStr(matrixFamiliaV)
        ),
        "talla"
      );
  const familiesRowOptions = lockFamiliesToArticulo
    ? familiasTallaRows.filter((f) => normStr(f.CODFAMTALLA) === resolvedFamilyV)
    : sortByKind(
        familiasTallaRows.filter(
          (f) =>
            !normStr(matrixFamiliaH) ||
            normStr(f.CODFAMTALLA) !== normStr(matrixFamiliaH)
        ),
        "color"
      );

  const normT = (s) => String(s ?? "").trim();

  const tallaLabel = (t) => {
    // Cabecera: dejar solo la talla corta (sin descripción larga).
    return normT(t?.CODTALLA);
  };

  const colorLabel = (t) => {
    // Filas: priorizar descripción (OLIVA) y, si no existe, quitar prefijo numérico del código.
    const d = normT(t?.DESCRIPCION);
    if (d) return d;
    const c = normT(t?.CODTALLA);
    return c.replace(/^\d+\s*[-/]?\s*/u, "").trim() || c;
  };

  const qtyForCell = (rowTalla, colTalla) => {
    const p = packLinepediFromGridPure(
      artForPack,
      matrixFamiliaH,
      matrixFamiliaV,
      colTalla,
      rowTalla,
    );
    if (!p) return 0;
    const r = lineasTallas.find((l) => lineasPackMatch(l, p));
    return r ? Number(r.unidades) || 0 : 0;
  };

  const updateMatrixCell = (rowTalla, colTalla, rawValue) => {
    const p = packLinepediFromGridPure(
      artForPack,
      matrixFamiliaH,
      matrixFamiliaV,
      colTalla,
      rowTalla,
    );
    if (!p) return;
    const unidades = toNumber(rawValue);
    setLineasTallas((prev) => {
      const idx = prev.findIndex((r) => lineasPackMatch(r, p));
      if (unidades <= 0) {
        if (idx < 0) return prev;
        return prev.filter((_, i) => i !== idx);
      }
      const nextRow = {
        codFamTallaH: p.codFamTallaH,
        codFamTallaV: p.codFamTallaV,
        codTallaH: normStr(p.codTallaH),
        codTallaV: normStr(p.codTallaV),
        unidades,
      };
      if (idx < 0) return [...prev, nextRow];
      const next = [...prev];
      next[idx] = nextRow;
      return next;
    });
  };

  const colTotals = useMemo(() => {
    if (!normStr(matrixFamiliaH) || !normStr(matrixFamiliaV)) return tallasH.map(() => 0);
    return tallasH.map((h) =>
      tallasV.reduce((sum, v) => {
        const p = packLinepediFromGridPure(
          artForPack,
          matrixFamiliaH,
          matrixFamiliaV,
          h,
          v,
        );
        const r = p ? lineasTallas.find((l) => lineasPackMatch(l, p)) : null;
        return sum + (r ? Number(r.unidades) || 0 : 0);
      }, 0),
    );
  }, [artForPack, tallasH, tallasV, lineasTallas, matrixFamiliaH, matrixFamiliaV]);

  useEffect(() => {
    if (!open || totalMatriz <= 0) return;
    setForm((p) => ({
      ...p,
      unidadesCompra: String(totalMatriz),
      unidadesVenta: String(totalMatriz),
      ...(isEdit ? {} : { modo: "ambos" }),
    }));
  }, [open, isEdit, totalMatriz]);

  const submitPedido = async () => {
    setSaving(true);
    setError("");
    try {
      await onConfirm?.(
        {
          ...form,
          lineasTallas,
        },
        { isEdit, modo: isEdit ? null : modoCreacion },
      );
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleConfirm = async () => {
    if (!form.articulo || !form.referencia || !form.fecha) {
      setError("Articulo, fecha y referencia son obligatorios.");
      return;
    }
    if (incluirCompra && !form.proveedor) {
      setError("Proveedor obligatorio para pedido de compra.");
      return;
    }
    if (incluirVenta && !form.cliente) {
      setError("Cliente obligatorio para pedido de venta.");
      return;
    }
    if (
      incluirCompra &&
      (toNumber(form.unidadesCompra) <= 0 || toNumber(form.precioCoste) < 0)
    ) {
      setError(
        "Unidades de compra (>0) y precio coste (>=0) son obligatorios.",
      );
      return;
    }
    if (
      incluirVenta &&
      (toNumber(form.unidadesVenta) <= 0 || toNumber(form.precioVenta) < 0)
    ) {
      setError("Unidades de venta (>0) y precio venta (>=0) son obligatorios.");
      return;
    }
    if (lineasTallas.length > 0) {
      if (!incluirCompra || !incluirVenta) {
        setError(
          "Si usas matriz talla/color, el pedido debe incluir compra y venta.",
        );
        return;
      }
      if (totalMatriz <= 0) {
        setError("La matriz debe tener unidades > 0.");
        return;
      }
      if (mismatchMatrizVsCabeceraPedido) {
        setConfirmMismatchDialogOpen(true);
        return;
      }
    }
    if (isEdit && pedidoTeniaMatriz && totalMatriz <= 0) {
      setError(
        "Este pedido tiene desglose talla/color en A3: rellena la rejilla (total > 0).",
      );
      return;
    }
    await submitPedido();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>
        {isEdit ? "Actualizar pedidos (A3)" : "Pedidos de Produccion (A3)"}
      </DialogTitle>
      <DialogContent>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={1}
          sx={{ mb: 1.5 }}
        >
          <Typography variant="body2" color="text.secondary">
            {isEdit
              ? "Puedes cambiar referencia, unidades, precios y fecha de entrega (compra y venta en A3 salvo que marques fechas distintas); proveedor y cliente deben coincidir con el pedido en A3."
              : modoCreacion === "ambos"
                ? "Se crean en A3 el pedido de compra y el de venta (misma referencia)."
                : modoCreacion === "compra"
                  ? "Solo se crea el pedido de compra (proveedor) en A3."
                  : "Solo se crea el pedido de venta (cliente) en A3."}
          </Typography>
          <Stack direction="row" spacing={1}>
            {!isEdit ? (
              <Chip
                size="small"
                label={`Empresa ${getEmpresaA3Meta(form.dbSource).shortLabel}`}
                sx={{
                  bgcolor: "rgba(25, 118, 210, 0.10)",
                  color: "info.dark",
                  fontWeight: 700,
                }}
              />
            ) : null}
            {incluirCompra ? (
              <Chip
                size="small"
                label={`Compra ${form.monedaCompra || "EUR"}`}
                sx={{
                  bgcolor: "rgba(211, 47, 47, 0.08)",
                  color: "error.main",
                  fontWeight: 700,
                }}
              />
            ) : null}
            {incluirVenta ? (
              <Chip
                size="small"
                label={`Venta ${form.monedaVenta || "EUR"}`}
                sx={{
                  bgcolor: "rgba(0, 194, 163, 0.12)",
                  color: "primary.main",
                  fontWeight: 700,
                }}
              />
            ) : null}
          </Stack>
        </Stack>
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        {!isEdit ? (
          <Box
            sx={{
              mb: 2,
              p: 1.2,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 0.75 }}
            >
              Tipo de alta en A3
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "2fr 1fr 1fr" },
                gap: 1,
                alignItems: "center",
              }}
            >
              <ToggleButtonGroup
                exclusive
                size="small"
                value={modoCreacion}
                onChange={(_, v) => {
                  if (v == null) return;
                  setForm((p) => {
                    const next = { ...p, modo: v };
                    if (v === "venta") {
                      next.unidadesCompra = "";
                      next.precioCoste = "";
                    } else if (v === "compra") {
                      next.unidadesVenta = "";
                      next.precioVenta = "";
                    } else if (v === "ambos") {
                      if (!String(next.unidadesCompra || "").trim()) {
                        next.unidadesCompra = String(presupuesto?.Units || 1);
                      }
                      if (!String(next.precioCoste || "").trim()) {
                        next.precioCoste = formatPriceInput(
                          articulo?.PRCCOMPRA ?? articulo?.PRCCOSTE,
                        );
                      }
                      if (!String(next.unidadesVenta || "").trim()) {
                        next.unidadesVenta = String(presupuesto?.Units || 1);
                      }
                      if (!String(next.precioVenta || "").trim()) {
                        next.precioVenta = formatPriceInput(articulo?.PRCVENTA);
                      }
                    }
                    return next;
                  });
                }}
                aria-label="tipo pedido A3"
              >
                <ToggleButton value="ambos">Compra + venta</ToggleButton>
                <ToggleButton value="compra">Solo compra</ToggleButton>
                <ToggleButton value="venta">Solo venta</ToggleButton>
              </ToggleButtonGroup>
              <TextField
                size="small"
                select
                label="Tipo contable"
                value={String(form.tipoContable || "1")}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    tipoContable: String(e.target.value || "1"),
                  }))
                }
                SelectProps={{ native: true }}
              >
                <option value="1">1</option>
                <option value="2">2</option>
              </TextField>
              <TextField
                size="small"
                select
                label="Base de datos A3"
                value={String(form.dbSource || "STAGE_DESIGN")}
                onChange={(e) =>
                  setForm((p) => {
                    if (lockDbSource) return p;
                    return {
                      ...p,
                      dbSource: String(e.target.value || "STAGE_DESIGN"),
                      proveedor: "",
                      proveedorNombre: "",
                      cliente: "",
                      clienteNombre: "",
                      centroCosteEmpresa: "",
                      centroCosteMarca: "",
                      centroCosteDepartamento: "",
                    };
                  })
                }
                SelectProps={{ native: true }}
                disabled={lockDbSource}
              >
                {A3_EMPRESA_DB_SOURCE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </TextField>
            </Box>
          </Box>
        ) : null}

        <Box
          sx={{
            mb: 1.5,
            p: 1.2,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              display: "block",
              fontWeight: 700,
              mb: 1,
            }}
          >
            Datos generales
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
              gap: 1.25,
            }}
          >
            <TextField
              label="Articulo"
              value={form.articulo}
              InputProps={{ readOnly: true }}
            />
            <TextField
              label="Referencia comun"
              value={form.referencia}
              onChange={(e) =>
                setForm((p) => ({ ...p, referencia: e.target.value }))
              }
              InputProps={{ readOnly: false }}
              disabled={false}
            />
            <TextField
              type="date"
              label={
                parCompraVentaEdit && !fechasDistintas
                  ? "Fecha entrega (compra y venta A3)"
                  : "Fecha"
              }
              InputLabelProps={{ shrink: true }}
              value={form.fecha}
              onChange={(e) =>
                setForm((p) =>
                  patchFechaEntregaPedido(p, "fecha", e.target.value, {
                    incluirCompra,
                    incluirVenta,
                    fechasDistintas,
                  }),
                )
              }
            />
          </Box>
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Chip
              size="small"
              label={`Un estimadas: ${unidadesEstimadasCabecera || 0}`}
              sx={{
                bgcolor: "rgba(25, 118, 210, 0.10)",
                color: "info.dark",
                fontWeight: 700,
              }}
            />
            <Chip
              size="small"
              label={`Un pedidas: ${unidadesPedidasCabecera || 0}`}
              sx={{
                bgcolor: "rgba(0, 194, 163, 0.12)",
                color: "primary.main",
                fontWeight: 700,
              }}
            />
          </Stack>
          {isEdit ? (
            <Box sx={{ mt: 1 }}>
              {parCompraVentaEdit ? (
                <FormControlLabel
                  sx={{ mb: 0.5, display: "block" }}
                  control={
                    <Checkbox
                      size="small"
                      checked={fechasDistintas}
                      onChange={(e) => {
                        const on = e.target.checked;
                        setFechasDistintas(on);
                        if (!on) {
                          const v = form.fechaVenta || form.fechaCompra || form.fecha;
                          if (v) {
                            setForm((p) =>
                              patchFechaEntregaPedido(p, "fecha", v, {
                                incluirCompra,
                                incluirVenta,
                                fechasDistintas: false,
                              }),
                            );
                          }
                        }
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2" color="text.secondary">
                      Fechas distintas en pedido compra y venta (A3)
                    </Typography>
                  }
                />
              ) : null}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                  gap: 1.25,
                }}
              >
                <TextField
                  type="date"
                  label="Fecha pedido compra (A3)"
                  InputLabelProps={{ shrink: true }}
                  value={form.fechaCompra || ""}
                  onChange={(e) =>
                    setForm((p) =>
                      patchFechaEntregaPedido(p, "fechaCompra", e.target.value, {
                        incluirCompra,
                        incluirVenta,
                        fechasDistintas,
                      }),
                    )
                  }
                  disabled={!incluirCompra || (parCompraVentaEdit && !fechasDistintas)}
                />
                <TextField
                  type="date"
                  label="Fecha pedido venta (A3)"
                  InputLabelProps={{ shrink: true }}
                  value={form.fechaVenta || ""}
                  onChange={(e) =>
                    setForm((p) =>
                      patchFechaEntregaPedido(p, "fechaVenta", e.target.value, {
                        incluirCompra,
                        incluirVenta,
                        fechasDistintas,
                      }),
                    )
                  }
                  disabled={!incluirVenta || (parCompraVentaEdit && !fechasDistintas)}
                />
              </Box>
            </Box>
          ) : null}
        </Box>

        <Box
          sx={{
            mb: 1.5,
            p: 1.2,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              display: "block",
              fontWeight: 700,
              mb: 0.5,
            }}
          >
            Centros de coste (analítica A3)
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 1 }}
          >
            Se graban en cabecera y líneas de compra y venta. Opciones del catálogo A3; por defecto se
            rellenan con la analítica del cliente.
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
              gap: 1.25,
            }}
          >
            <Autocomplete
              size="small"
              options={empresaCentroOptions}
              value={
                empresaCentroOptions.find(
                  (o) =>
                    o.code === normCentroCosteCod(form.centroCosteEmpresa || ""),
                ) || null
              }
              onChange={(_, option) =>
                setForm((p) => ({
                  ...p,
                  centroCosteEmpresa: option?.code || "",
                }))
              }
              isOptionEqualToValue={(opt, val) => opt.code === val.code}
              getOptionLabel={(option) => option?.label || ""}
              renderInput={(params) => (
                <TextField {...params} label="Empresa" placeholder="Buscar..." />
              )}
            />
            <Autocomplete
              size="small"
              options={marcaCentroOptions}
              value={
                marcaCentroOptions.find(
                  (o) => o.code === normCentroCosteCod(form.centroCosteMarca || ""),
                ) || null
              }
              onChange={(_, option) =>
                setForm((p) => ({
                  ...p,
                  centroCosteMarca: option?.code || "",
                }))
              }
              isOptionEqualToValue={(opt, val) => opt.code === val.code}
              getOptionLabel={(option) => option?.label || ""}
              renderInput={(params) => (
                <TextField {...params} label="Marca" placeholder="Buscar..." />
              )}
            />
            <Autocomplete
              size="small"
              options={departamentoCentroOptions}
              value={
                departamentoCentroOptions.find(
                  (o) =>
                    o.code === normCentroCosteCod(form.centroCosteDepartamento || ""),
                ) || null
              }
              onChange={(_, option) =>
                setForm((p) => ({
                  ...p,
                  centroCosteDepartamento: option?.code || "",
                }))
              }
              isOptionEqualToValue={(opt, val) => opt.code === val.code}
              getOptionLabel={(option) => option?.label || ""}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Departamento"
                  placeholder="Buscar..."
                />
              )}
            />
          </Box>
        </Box>

        <Box
          sx={{
            mb: 1.5,
            p: 1.2,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              display: "block",
              fontWeight: 700,
              mb: 0.5,
            }}
          >
            Matriz tallas / colores (opcional)
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
            Arriba = familia de la primera lista, izquierda = la segunda. A3 guarda{" "}
            <strong>CODFAMTALLAH/CODTALLAH</strong> (horizontal en ERP) y{" "}
            <strong>CODFAMTALLAV/CODTALLAV</strong> (vertical): si el maestro del artículo tiene{" "}
            <em>dos familias distintas</em>, las cantidades se graban en la combinación correcta
            (tallas arriba / colores al lado cuando corresponde). El total se sincroniza con compra/venta.
          </Typography>
          {mostrarMatriz ? (
            <Alert severity="info" sx={{ mb: 1 }} icon={false}>
              <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.25 }}>
                Moneda del pedido en A3
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                Compra: <strong>{etiquetaMonedaCorta(form.monedaCompra)}</strong>
                {" · "}
                Venta: <strong>{etiquetaMonedaCorta(form.monedaVenta)}</strong>
                . Los importes de compra/venta (precio unitario y totales) van en esa moneda; la rejilla
                solo muestra <strong>unidades</strong>.
              </Typography>
            </Alert>
          ) : null}
          {!mostrarMatriz ? (
            <Typography variant="caption" color="text.secondary">
              Disponible solo en alta con compra + venta, o al editar cuando existen ambos pedidos.
            </Typography>
          ) : (
            <>
              {loadingMatrizPedido ? (
                <Alert severity="info" sx={{ mb: 1 }}>
                  Cargando desglose desde A3…
                </Alert>
              ) : null}
              {normStr(matrixFamiliaH) &&
              normStr(matrixFamiliaV) &&
              normStr(matrixFamiliaH) === normStr(matrixFamiliaV) ? (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  Has la <strong>misma familia</strong> arriba y a la izquierda: la rejilla mostrará los
                  mismos códigos en ambos ejes. Para <strong>tallas arriba y colores al lado</strong>, el
                  artículo en A3 debe tener <strong>CODFAMTALLAH</strong> y <strong>CODFAMTALLAV</strong>{" "}
                  distintos (o elige manualmente otra familia en uno de los desplegables).
                </Alert>
              ) : null}
              {mismatchMatrizVsCabeceraPedido ? (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  La suma de la matriz ({totalMatriz}) no coincide con Units de la cabecera
                  del pedido (Units: {unitsCabeceraPedido}).
                </Alert>
              ) : null}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
                  gap: 1.25,
                  mb: 1.25,
                }}
              >
                <TextField
                  label="Familia de tallas (columnas)"
                  value={matrixFamiliaH}
                  select
                  SelectProps={{ native: true }}
                  disabled={lockFamiliesToArticulo}
                  onChange={(e) => {
                    setMatrixFamiliaH(e.target.value);
                    setLineasTallas([]);
                  }}
                >
                  <option value=""></option>
                  {familiesColumnOptions.map((f) => (
                    <option key={`fh-${f.CODFAMTALLA}`} value={f.CODFAMTALLA}>
                      {f.CODFAMTALLA} - {f.DESCFAMTALLA}
                    </option>
                  ))}
                </TextField>
                <TextField
                  label="Familia de colores (filas)"
                  value={matrixFamiliaV}
                  select
                  SelectProps={{ native: true }}
                  disabled={lockFamiliesToArticulo}
                  onChange={(e) => {
                    setMatrixFamiliaV(e.target.value);
                    setLineasTallas([]);
                  }}
                >
                  <option value=""></option>
                  {familiesRowOptions.map((f) => (
                    <option key={`fv-${f.CODFAMTALLA}`} value={f.CODFAMTALLA}>
                      {f.CODFAMTALLA} - {f.DESCFAMTALLA}
                    </option>
                  ))}
                </TextField>
              </Box>
              {lockFamiliesToArticulo ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 0.75 }}
                >
                  Familias bloqueadas según artículo A3: columnas{" "}
                  <strong>{resolvedFamilyH}</strong>, filas <strong>{resolvedFamilyV}</strong>.
                </Typography>
              ) : null}
              {!matrixFamiliaH || !matrixFamiliaV ? (
                <Typography variant="caption" color="text.secondary">
                  Elige las dos familias de la matriz (coinciden con el maestro A3 del artículo).
                </Typography>
              ) : tallasH.length === 0 || tallasV.length === 0 ? (
                <Typography variant="caption" color="text.secondary">
                  No hay tallas en catálogo para esas familias (o aún se están cargando).
                </Typography>
              ) : (
                <Box sx={{ overflowX: "auto", maxWidth: "100%" }}>
                  <Table size="small" sx={{ minWidth: 520 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{
                            position: "sticky",
                            left: 0,
                            bgcolor: "background.paper",
                            zIndex: 1,
                            fontWeight: 700,
                          }}
                        >
                          Lateral / Superior
                        </TableCell>
                        {tallasH.map((h) => (
                          <TableCell key={`ch-${String(h.CODTALLA)}`} align="right">
                            <Typography variant="caption" sx={{ fontWeight: 600 }}>
                              {tallaLabel(h)}
                            </Typography>
                          </TableCell>
                        ))}
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          Σ fila (uds)
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tallasV.map((v) => {
                        const rowSum = tallasH.reduce((s, h) => s + qtyForCell(v, h), 0);
                        return (
                          <TableRow key={`vr-${String(v.CODTALLA)}`}>
                            <TableCell
                              sx={{
                                position: "sticky",
                                left: 0,
                                bgcolor: "background.paper",
                                zIndex: 1,
                                whiteSpace: "nowrap",
                                fontWeight: 600,
                              }}
                            >
                              {colorLabel(v)}
                            </TableCell>
                            {tallasH.map((h) => {
                              const q = qtyForCell(v, h);
                              return (
                                <TableCell
                                  key={`c-${String(v.CODTALLA)}-${String(h.CODTALLA)}`}
                                  align="right"
                                  sx={{ py: 0.5, minWidth: 72 }}
                                >
                                  <TextField
                                    size="small"
                                    value={q > 0 ? String(q) : ""}
                                    placeholder="0"
                                    inputProps={{
                                      inputMode: "decimal",
                                      style: { textAlign: "right", padding: "4px 6px" },
                                    }}
                                    onChange={(e) =>
                                      updateMatrixCell(v, h, e.target.value)
                                    }
                                  />
                                </TableCell>
                              );
                            })}
                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                              {fmtQtyMatriz(rowSum)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Σ columna (uds)</TableCell>
                        {colTotals.map((ct, i) => (
                          <TableCell key={`ft-${i}`} align="right" sx={{ fontWeight: 600 }}>
                            {fmtQtyMatriz(ct)}
                          </TableCell>
                        ))}
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          {fmtQtyMatriz(totalMatriz)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Box>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                Total matriz: <strong>{fmtQtyMatriz(totalMatriz)} uds</strong>
                {pedidoTeniaMatriz ? " · pedido con desglose en A3" : ""}
              </Typography>
            </>
          )}
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              p: 1.25,
              border: "1px solid",
              borderColor: incluirCompra ? "error.light" : "divider",
              borderRadius: 1,
              bgcolor: incluirCompra
                ? "rgba(211, 47, 47, 0.04)"
                : "transparent",
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 1 }}
            >
              <Typography
                variant="subtitle2"
                sx={{ color: "error.main", fontWeight: 800 }}
              >
                COMPRA
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                Total:{" "}
                <strong>
                  {fmtMoney(totalCoste)} {etiquetaMonedaCorta(form.monedaCompra)}
                </strong>
              </Typography>
            </Stack>
            <Divider sx={{ mb: 1.2 }} />
            {incluirCompra ? (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 1.25,
                }}
              >
                <TextField
                  label="Proveedor"
                  value={form.proveedor}
                  select
                  SelectProps={{ native: true, readOnly: isEdit }}
                  onChange={(e) => {
                    const code = e.target.value;
                    const selected = proveedores.find(
                      (item) =>
                        a3ProveedorCodigo(item) === String(code || "").trim(),
                    );
                    setForm((p) => ({
                      ...p,
                      proveedor: code,
                      proveedorNombre: a3ProveedorNombre(selected),
                    }));
                  }}
                  disabled={isEdit}
                >
                  <option value=""></option>
                  {form.proveedor &&
                  !proveedores.some(
                    (item) =>
                      a3ProveedorCodigo(item) ===
                      String(form.proveedor || "").trim(),
                  ) ? (
                    <option value={form.proveedor}>{form.proveedor}</option>
                  ) : null}
                  {sortedProveedores.map((item) => {
                    const c = a3ProveedorCodigo(item);
                    const n = a3ProveedorNombre(item);
                    return (
                      <option key={`prov-${c}`} value={c}>
                        {n ? `${n} (${c})` : c}
                      </option>
                    );
                  })}
                </TextField>
                <TextField
                  label="Moneda compra"
                  value={form.monedaCompra}
                  select
                  SelectProps={{ native: true }}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, monedaCompra: e.target.value }))
                  }
                  disabled={isEdit}
                >
                  <option value="EUR">EUR</option>
                  <option value="USD">USD</option>
                </TextField>
                <TextField
                  label="Unidades compra"
                  value={form.unidadesCompra}
                  inputProps={{ inputMode: "decimal" }}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, unidadesCompra: e.target.value }))
                  }
                />
                <TextField
                  label="Precio coste (UN)"
                  value={form.precioCoste}
                  inputProps={{ inputMode: "decimal", placeholder: "0.00" }}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, precioCoste: e.target.value }))
                  }
                />
                <TextField
                  label={`Coste total (${etiquetaMonedaCorta(form.monedaCompra)})`}
                  value={fmtMoney(totalCoste)}
                  InputProps={{ readOnly: true }}
                  sx={{ gridColumn: "1 / -1" }}
                />
              </Box>
            ) : (
              <Typography variant="caption" color="text.secondary">
                No aplica en este modo.
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              p: 1.25,
              border: "1px solid",
              borderColor: incluirVenta ? "primary.main" : "divider",
              borderRadius: 1,
              bgcolor: incluirVenta ? "rgba(0, 194, 163, 0.06)" : "transparent",
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 1 }}
            >
              <Typography
                variant="subtitle2"
                sx={{ color: "primary.main", fontWeight: 800 }}
              >
                VENTA
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                Total:{" "}
                <strong>
                  {fmtMoney(totalVenta)} {etiquetaMonedaCorta(form.monedaVenta)}
                </strong>
              </Typography>
            </Stack>
            <Divider sx={{ mb: 1.2 }} />
            {incluirVenta ? (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 1.25,
                }}
              >
                <TextField
                  label="Cliente"
                  value={form.cliente}
                  select
                  SelectProps={{ native: true, readOnly: isEdit }}
                  onChange={(e) => {
                    const code = e.target.value;
                    const selected = clientesA3.find(
                      (item) =>
                        String(item.CODCLI || "").trim() ===
                        String(code || "").trim(),
                    );
                    setForm((p) => ({
                      ...p,
                      cliente: code,
                      clienteNombre: String(selected?.NOMBRE || "").trim(),
                    }));
                  }}
                  disabled={isEdit}
                >
                  <option value=""></option>
                  {form.cliente &&
                  !clientesA3.some(
                    (item) =>
                      String(item.CODCLI || "").trim() ===
                      String(form.cliente || "").trim(),
                  ) ? (
                    <option value={form.cliente}>{form.cliente}</option>
                  ) : null}
                  {sortedClientesA3.map((item) => (
                    <option key={`cli-${item.CODCLI}`} value={item.CODCLI}>
                      {String(item.NOMBRE || "").trim()
                        ? `${item.NOMBRE} (${item.CODCLI})`
                        : String(item.CODCLI || "").trim()}
                    </option>
                  ))}
                </TextField>
                <TextField
                  label="Moneda venta"
                  value={form.monedaVenta}
                  select
                  SelectProps={{ native: true }}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, monedaVenta: e.target.value }))
                  }
                  disabled={isEdit}
                >
                  <option value="EUR">EUR</option>
                  <option value="USD">USD</option>
                </TextField>
                <TextField
                  label="Unidades venta"
                  value={form.unidadesVenta}
                  inputProps={{ inputMode: "decimal" }}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, unidadesVenta: e.target.value }))
                  }
                />
                <TextField
                  label="Precio venta (UN)"
                  value={form.precioVenta}
                  inputProps={{ inputMode: "decimal", placeholder: "0.00" }}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, precioVenta: e.target.value }))
                  }
                />
                <TextField
                  label={`Venta total (${etiquetaMonedaCorta(form.monedaVenta)})`}
                  value={fmtMoney(totalVenta)}
                  InputProps={{ readOnly: true }}
                  sx={{ gridColumn: "1 / -1" }}
                />
              </Box>
            ) : (
              <Typography variant="caption" color="text.secondary">
                No aplica en este modo.
              </Typography>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={handleConfirm} disabled={saving}>
          {isEdit ? "Guardar cambios" : "Crear en A3"}
        </Button>
      </DialogActions>
      <Dialog
        open={confirmMismatchDialogOpen}
        onClose={() => setConfirmMismatchDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Revisar unidades de matriz</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            La suma de tallas ({totalMatriz}) no coincide con Units en cabecera del
            pedido ({unitsCabeceraPedido}).
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmMismatchDialogOpen(false)}
            disabled={saving}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            disabled={saving}
            onClick={async () => {
              setConfirmMismatchDialogOpen(false);
              await submitPedido();
            }}
          >
            Guardar igualmente
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default ProduccionPedidosDialog;
